import { z } from 'zod';
import { hqHalfyearTopicIdSchema } from './hq-halfyear-review';

const hash = z.string().regex(/^[a-f0-9]{64}$/u);
const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
const text = z.string().max(100000);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).refine(value => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
});
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const half = z.union([z.literal(1), z.literal(2)]);
const source = z.object({ sourceId: id, sha256: hash, location: z.string().min(1).max(300), text: text.nullable() }).strict();
const activity = z.object({
  siteId: id.nullable(), division: id.nullable(), title: z.string().min(1).max(500), date: date.nullable(),
  reportedState: text.nullable(),
}).strict();
const monthly = z.object({
  round: count, month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u), meetingDate: date.nullable(),
  dateCandidates: z.array(date).min(1).max(2), place: text.nullable(), authorName: text.nullable(),
  listedAttendees: z.array(z.object({ name: z.string().max(200), organization: z.string().max(200) }).strict()).max(200),
  confirmedAttendanceCount: z.null(),
  minutes: z.array(z.object({ address: z.string().max(20), text }).strict()).max(200),
}).strict();
const review = z.object({
  half, reportDate: date, reportNumber: z.string().max(200),
  topics: z.array(z.object({ id: hqHalfyearTopicIdSchema, requirement: text, narrative: text.nullable(),
    originalJudgement: z.enum(['양호', '개선필요', '보완', '미흡', '불량']) }).strict()).length(10),
}).strict().refine(row => new Set(row.topics.map(topic => topic.id)).size === 10);
const emphasis = z.object({
  half, round: count, reportDate: date, eventFrom: date, eventTo: date,
  overallFindings: count.nullable(), overallActions: count.nullable(),
  siteInspections: z.array(z.object({ siteId: id, findings: count.nullable(), actions: count.nullable(),
    findingDate: date.nullable(), actionReportedAt: date.nullable() }).strict()).max(200),
  trainingActivities: z.array(activity).max(200), confirmedTrainingCount: count.nullable(),
  campaignActivities: z.array(activity).max(200), confirmedCampaignCount: count.nullable(),
  contests: z.array(z.object({ id, title: z.string().max(500), submissions: count }).strict()).max(200),
  submissionTotal: count,
}).strict().refine(row => row.contests.reduce((sum, item) => sum + item.submissions, 0) === row.submissionTotal);
const inspection = z.object({
  siteId: id.nullable(), division: id, plannedFrom: date.nullable(), plannedTo: date.nullable(),
  actualFrom: date.nullable(), actualTo: date.nullable(), findings: count.nullable(), actions: count.nullable(),
  actionReportedAt: date.nullable(),
  reportedBySite: z.array(z.object({ siteId: id, findings: count }).strict()).max(200),
}).strict();
const common = {
  schemaVersion: z.literal(1), tenantId: z.literal('kop'), scope: z.literal('headquarters'),
  id, year: z.number().int().min(2000).max(2200), lifecycle: z.literal('historical-source'),
  title: z.string().min(1).max(500), sourceRefs: z.array(source).min(1).max(2000),
  heldFields: z.array(text).max(200), originalAttachmentState: z.literal('not-uploaded'),
  provenance: z.object({ batchId: id, packetHash: hash, mappingHash: hash, authorityHash: hash,
    importedAt: z.string().datetime(), actorKind: z.literal('authorized-operator') }).strict(),
};
export const hqHistoricalReportBodySchema = z.discriminatedUnion('kind', [
  z.object({ ...common, kind: z.literal('monthly-meeting'), data: monthly }).strict(),
  z.object({ ...common, kind: z.literal('halfyear-review'), data: review }).strict(),
  z.object({ ...common, kind: z.literal('emphasis-week'), data: emphasis }).strict(),
  z.object({ ...common, kind: z.literal('inspection-report'), data: inspection }).strict(),
]);
export type HqHistoricalReportBody = z.infer<typeof hqHistoricalReportBodySchema>;
export const hqHistoricalSummarySchema = z.object({
  schemaVersion: z.literal(1), tenantId: z.literal('kop'), year: z.number().int(), batchId: id,
  savedAt: z.string().datetime(),
  monthlyMeetings: z.array(z.object({ id, title: z.string(), round: count, month: z.string(), meetingDate: date.nullable() }).strict()).max(100),
  halfyear: z.array(z.object({ id, half, reportDate: date, topics: review.shape.topics }).strict()).max(2),
  emphasis: z.array(z.object({ id, ...emphasis.shape }).strict()).max(10),
  inspections: z.array(z.object({ id, title: z.string(), ...inspection.shape }).strict()).max(200),
}).strict();
export type HqHistoricalSummary = z.infer<typeof hqHistoricalSummarySchema>;
